import { useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Clock,
  Banknote,
} from "lucide-react";
import { formatAmount } from "@/utils/formatAmount";
import { formatDate } from "@/utils/formatDate";
import { useOverduePenalties } from "@/features/orders/hooks/useOrders";
import {
  useForgiveDaily,
  useSettlePenalties,
} from "@/features/orders/hooks/useOrders";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { PageLoader } from "@/components/ui/Spinner";
import apiClient from "@/api/client";
import { useQueryClient } from "@tanstack/react-query";
import type {
  OverduePenaltyRow,
  OverduePenaltyTerm,
} from "@/features/orders/types";

export function PenaltiesPage() {
  const { data: rows, isLoading } = useOverduePenalties();
  const forgiveDaily = useForgiveDaily();
  const settlePenalties = useSettlePenalties();
  const qc = useQueryClient();

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Forgive modal
  const [forgiveModal, setForgiveModal] = useState<OverduePenaltyRow | null>(
    null,
  );
  const [forgiveNote, setForgiveNote] = useState("");
  const [forgiveError, setForgiveError] = useState("");

  // Settle modal
  const [settleModal, setSettleModal] = useState<OverduePenaltyRow | null>(
    null,
  );
  const [settleNote, setSettleNote] = useState("");
  const [settleAmount, setSettleAmount] = useState("");
  const [settleError, setSettleError] = useState("");
  const [settleSubmitting, setSettleSubmitting] = useState(false);

  // One-time penalty apply modal
  const [penaltyModal, setPenaltyModal] = useState<{
    row: OverduePenaltyRow;
    term: OverduePenaltyTerm;
  } | null>(null);
  const [penaltyAmount, setPenaltyAmount] = useState("");
  const [penaltyNote, setPenaltyNote] = useState("");
  const [penaltySubmitting, setPenaltySubmitting] = useState(false);
  const [penaltyError, setPenaltyError] = useState("");

  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const openForgive = (row: OverduePenaltyRow) => {
    setForgiveNote("");
    setForgiveError("");
    setForgiveModal(row);
  };

  const submitForgive = () => {
    if (!forgiveModal) return;
    setForgiveError("");
    forgiveDaily.mutate(
      { id: forgiveModal.order_id, note: forgiveNote },
      {
        onSuccess: () => setForgiveModal(null),
        onError: () =>
          setForgiveError("Błąd podczas zapisywania. Spróbuj ponownie."),
      },
    );
  };

  const openSettle = (row: OverduePenaltyRow) => {
    const pending =
      parseFloat(row.accumulated_daily_amount) - parseFloat(row.settled_amount);
    setSettleAmount(pending.toFixed(2));
    setSettleNote("");
    setSettleError("");
    setSettleModal(row);
  };

  const submitSettle = async () => {
    if (!settleModal) return;
    setSettleSubmitting(true);
    setSettleError("");
    settlePenalties.mutate(
      {
        id: settleModal.order_id,
        settlement_note: settleNote,
        amount: settleAmount,
      },
      {
        onSuccess: () => {
          setSettleModal(null);
          setSettleSubmitting(false);
        },
        onError: () => {
          setSettleError("Błąd podczas zapisywania. Sprawdź dane.");
          setSettleSubmitting(false);
        },
      },
    );
  };

  const openPenaltyModal = (
    row: OverduePenaltyRow,
    term: OverduePenaltyTerm,
  ) => {
    setPenaltyAmount(term.calculated_amount);
    setPenaltyNote("");
    setPenaltyError("");
    setPenaltyModal({ row, term });
  };

  const submitPenalty = async () => {
    if (!penaltyModal?.row.agreement_id) return;
    setPenaltySubmitting(true);
    setPenaltyError("");
    try {
      await apiClient.post(
        `/agreements/${penaltyModal.row.agreement_id}/penalty-records/`,
        {
          order: penaltyModal.row.order_id,
          penalty_term: penaltyModal.term.term_id,
          delay_days: penaltyModal.row.days_overdue,
          amount: penaltyAmount,
          note: penaltyNote,
          is_termination: false,
        },
      );
      qc.invalidateQueries({ queryKey: ["orders", "overdue-penalties"] });
      setPenaltyModal(null);
    } catch {
      setPenaltyError("Błąd podczas zapisywania kary.");
    } finally {
      setPenaltySubmitting(false);
    }
  };

  return (
    <div>
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
        Kary umowne
      </h1>
        <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-6" />
      </div>
      {/* <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Opóźnione zamówienia — kary za opóźnienie naliczane automatycznie; kary jednorazowe wymagają
        decyzji dyrektora.
      </p> */}

      {isLoading ? (
        <PageLoader />
      ) : (rows ?? []).length === 0 ? (
        <div className="bg-white dark:bg-gray-800 p-8 text-center">
          <p className="text-gray-400 dark:text-gray-500 text-sm">
            Brak opóźnionych zamówień — wszystko na bieżąco.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {(rows ?? []).map((row) => (
            <PenaltyRow
              key={row.order_id}
              row={row}
              isExpanded={expanded.has(row.order_id)}
              onToggleExpand={() => toggleExpand(row.order_id)}
              onForgive={() => openForgive(row)}
              onSettle={() => openSettle(row)}
              onNalicz={(term) => openPenaltyModal(row, term)}
            />
          ))}
        </div>
      )}

      {/* Forgive daily penalty modal */}
      <Modal
        open={!!forgiveModal}
        onClose={() => setForgiveModal(null)}
        title="Daruj karę za opóźnienie"
      >
        <div className="flex flex-col gap-4">
          {forgiveModal && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <p className="font-medium mb-1">
                {forgiveModal.order_number} — {forgiveModal.contractor_name}
              </p>
              <p className="text-xs text-amber-700">
                Naliczone automatycznie:{" "}
                <strong>
                  {formatAmount(forgiveModal.accumulated_daily_amount)} PLN
                </strong>
                . Darowując karę, rezygnujesz z jej pobierania i zatrzymujesz
                dalsze naliczanie.
              </p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Uzasadnienie darowania kary
            </label>
            <textarea
              rows={3}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={forgiveNote}
              onChange={(e) => setForgiveNote(e.target.value)}
              placeholder="np. ugoda z podwykonawcą, przyczyny obiektywne..."
            />
          </div>
          {forgiveError && (
            <p className="text-sm text-red-600">{forgiveError}</p>
          )}
          <div className="flex gap-3">
            <Button
              loading={forgiveDaily.isPending}
              onClick={submitForgive}
              variant="secondary"
            >
              Daruj karę
            </Button>
            <Button variant="secondary" onClick={() => setForgiveModal(null)}>
              Anuluj
            </Button>
          </div>
        </div>
      </Modal>

      {/* Settle modal */}
      <Modal
        open={!!settleModal}
        onClose={() => setSettleModal(null)}
        title="Oznacz karę jako uregulowaną"
      >
        <div className="flex flex-col gap-4">
          {settleModal && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
              <p className="font-medium mb-1">{settleModal.order_number}</p>
              <p className="text-xs text-green-700">
                Uregulowanie kary zapisze wpływ jako przychód dla
                przedsięwzięcia <strong>{settleModal.project_name}</strong>.
              </p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Kwota uregulowana (PLN)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={settleAmount}
              onChange={(e) => setSettleAmount(e.target.value)}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Sposób uregulowania
            </label>
            <textarea
              rows={3}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={settleNote}
              onChange={(e) => setSettleNote(e.target.value)}
              placeholder="np. przelew bankowy 15.04.2026, potrącenie z faktury..."
            />
          </div>
          {settleError && <p className="text-sm text-red-600">{settleError}</p>}
          <div className="flex gap-3">
            <Button loading={settleSubmitting} onClick={submitSettle}>
              Zapisz uregulowanie
            </Button>
            <Button variant="secondary" onClick={() => setSettleModal(null)}>
              Anuluj
            </Button>
          </div>
        </div>
      </Modal>

      {/* One-time penalty apply modal */}
      <Modal
        open={!!penaltyModal}
        onClose={() => setPenaltyModal(null)}
        title="Nalicz karę jednorazową"
      >
        <div className="flex flex-col gap-4">
          {penaltyModal && (
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 text-sm text-gray-600 dark:text-gray-400">
              <span className="font-medium text-gray-800 dark:text-gray-200">
                {penaltyModal.row.order_number}
              </span>
              {" · "}opóźnienie {penaltyModal.row.days_overdue} dni
              {" · "}
              <span className="italic">{penaltyModal.term.description}</span>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Kwota kary (PLN)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={penaltyAmount}
              onChange={(e) => setPenaltyAmount(e.target.value)}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Uzasadnienie
            </label>
            <textarea
              rows={3}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={penaltyNote}
              onChange={(e) => setPenaltyNote(e.target.value)}
              placeholder="Podstawa naliczenia kary..."
            />
          </div>
          {penaltyError && (
            <p className="text-sm text-red-600">{penaltyError}</p>
          )}
          <div className="flex gap-3">
            <Button loading={penaltySubmitting} onClick={submitPenalty}>
              Nalicz karę
            </Button>
            <Button variant="secondary" onClick={() => setPenaltyModal(null)}>
              Anuluj
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

interface PenaltyRowProps {
  row: OverduePenaltyRow;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onForgive: () => void;
  onSettle: () => void;
  onNalicz: (term: OverduePenaltyTerm) => void;
}

function PenaltyRow({
  row,
  isExpanded,
  onToggleExpand,
  onForgive,
  onSettle,
  onNalicz,
}: PenaltyRowProps) {
  const delayTerms = row.terms.filter((t) => t.is_delay_penalty);
  const oneTimeTerms = row.terms.filter((t) => !t.is_delay_penalty);
  const hasTerms = row.terms.length > 0;

  const accumulatedDaily = parseFloat(row.accumulated_daily_amount || "0");
  const settled = parseFloat(row.settled_amount || "0");
  const pendingDaily = accumulatedDaily - settled;

  // Header color: forgiven = gray, has delay = red, only one-time = amber
  const hasDelayTerms = delayTerms.length > 0;
  const headerBg = row.daily_penalty_forgiven
    ? "bg-gray-50 dark:bg-gray-800/50"
    : hasDelayTerms
      ? "bg-red-50"
      : "bg-amber-50";
  const borderColor = row.daily_penalty_forgiven
    ? "border-gray-200 dark:border-gray-700"
    : hasDelayTerms
      ? "border-red-200"
      : "border-amber-200";

  const summaryBadges = (
    <>
      {row.daily_penalty_forgiven ? (
        <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
          <XCircle size={12} /> Darowano
        </span>
      ) : hasDelayTerms && accumulatedDaily > 0 ? (
        <div className="text-right">
          <p className="text-xs text-gray-500 dark:text-gray-400">Naliczone</p>
          <p className="text-sm font-bold text-red-700">
            {formatAmount(row.accumulated_daily_amount)} PLN
          </p>
        </div>
      ) : hasDelayTerms ? (
        <div className="text-right">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Kara dzienna
          </p>
          <p className="text-sm font-bold text-red-700">
            {formatAmount(
              delayTerms
                .reduce((s, t) => s + parseFloat(t.calculated_amount), 0)
                .toFixed(2),
            )}{" "}
            PLN
          </p>
        </div>
      ) : null}
      {settled > 0 && (
        <span className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded-full">
          <CheckCircle2 size={12} /> {formatAmount(row.settled_amount)} PLN
          uregulowane
        </span>
      )}
      {!hasTerms && !row.agreement_id && (
        <span className="text-xs text-gray-400 dark:text-gray-500 italic">
          brak umowy
        </span>
      )}
      {!hasTerms && row.agreement_id && (
        <span className="text-xs text-gray-400 dark:text-gray-500 italic">
          brak kar w umowie
        </span>
      )}
    </>
  );

  return (
    <div className={`rounded-xl border overflow-hidden ${borderColor}`}>
      {/* Header */}
      <div className={`px-3 sm:px-4 py-3 ${headerBg}`}>
        {/* Top row: chevron + days badge + (desktop) main info + (desktop) badges */}
        <div className="flex items-start sm:items-center gap-2 sm:gap-3">
          <button
            onClick={onToggleExpand}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 flex-shrink-0 mt-0.5 sm:mt-0"
            aria-label={isExpanded ? "Zwiń" : "Rozwiń"}
          >
            {isExpanded ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronRight size={16} />
            )}
          </button>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            <AlertTriangle
              size={15}
              className={
                row.daily_penalty_forgiven
                  ? "text-gray-400 dark:text-gray-500"
                  : "text-red-500"
              }
            />
            <span
              className={`text-sm font-semibold ${
                row.daily_penalty_forgiven
                  ? "text-gray-500 dark:text-gray-400"
                  : "text-red-700"
              }`}
            >
              {row.days_overdue}{" "}
              {row.days_overdue === 1 ? "dzień" : "dni"}
            </span>
          </div>

          {/* Mobile: just order number on top row */}
          <Link
            to={`/orders/${row.order_id}`}
            className="sm:hidden text-sm font-semibold text-[var(--color-primary)] dark:text-blue-300 hover:underline min-w-0 truncate flex-1"
          >
            {row.order_number}
          </Link>

          {/* Desktop: full main info block */}
          <div className="hidden sm:block flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Link
                to={`/orders/${row.order_id}`}
                className="text-sm font-semibold text-[var(--color-primary)] dark:text-blue-300 hover:underline"
              >
                {row.order_number}
              </Link>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {row.contractor_name}
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                · {row.project_name}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Termin: {formatDate(row.deadline)}
              </span>
              {row.agreement_title && (
                <Link
                  to={`/agreements/${row.agreement_id}`}
                  className="text-xs text-[var(--color-primary)] hover:underline"
                >
                  {row.agreement_title}
                </Link>
              )}
            </div>
          </div>

          {/* Desktop badges */}
          <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
            {summaryBadges}
          </div>
        </div>

        {/* Mobile: stacked details */}
        <div className="sm:hidden mt-2 pl-7 flex flex-col gap-1">
          <div className="text-xs text-gray-600 dark:text-gray-400 break-words">
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {row.contractor_name}
            </span>
            <span className="text-gray-400 dark:text-gray-500">
              {" · "}
              {row.project_name}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="text-gray-500 dark:text-gray-400">
              Termin: {formatDate(row.deadline)}
            </span>
            {row.agreement_title && (
              <Link
                to={`/agreements/${row.agreement_id}`}
                className="text-[var(--color-primary)] hover:underline truncate"
              >
                {row.agreement_title}
              </Link>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            {summaryBadges}
          </div>
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="bg-white dark:bg-gray-800 px-4 py-4 border-t border-gray-100 flex flex-col gap-5">
          {!hasTerms ? (
            <p className="text-sm text-gray-400 dark:text-gray-500 italic py-2">
              {row.agreement_id
                ? "Brak zdefiniowanych kar w powiązanej umowie ramowej."
                : "To zamówienie nie jest powiązane z żadną umową ramową."}
            </p>
          ) : (
            <>
              {/* DAILY PENALTY SECTION */}
              {delayTerms.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                    Kary za opóźnienie (naliczane automatycznie)
                  </p>

                  {/* Per-term rate info */}
                  <div className="space-y-2 mb-3">
                    {delayTerms.map((term) => (
                      <div
                        key={term.term_id}
                        className="flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-800/50 rounded-lg px-3 py-2"
                      >
                        <div>
                          <span className="text-gray-800 dark:text-gray-200 font-medium">
                            {term.description}
                          </span>
                          {term.termination_possible && (
                            <span className="ml-2 text-xs text-red-600 font-medium">
                              (zerwanie możliwe)
                            </span>
                          )}
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {formatAmount(term.daily_amount!)} PLN/dzień
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Wyliczone łącznie
                          </p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {formatAmount(term.calculated_amount)} PLN
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Status + actions */}
                  {row.daily_penalty_forgiven ? (
                    <div className="bg-gray-50 dark:bg-gray-800/50 px-4 py-3 flex items-start gap-3">
                      <XCircle
                        size={18}
                        className="text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Kara darowana
                        </p>
                        {row.daily_penalty_forgiven_note && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {row.daily_penalty_forgiven_note}
                          </p>
                        )}
                        {settled > 0 && (
                          <p className="text-xs text-green-700 mt-1">
                            Uregulowane przed darowaniem:{" "}
                            {formatAmount(row.settled_amount)} PLN
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Clock size={16} className="text-red-500" />
                          <span className="text-sm font-medium text-red-800">
                            Naliczone automatycznie:{" "}
                            <strong>
                              {formatAmount(row.accumulated_daily_amount)} PLN
                            </strong>
                          </span>
                        </div>
                        {settled > 0 && (
                          <span className="text-xs text-green-700">
                            Uregulowane: {formatAmount(row.settled_amount)} PLN
                          </span>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 flex-wrap">
                        {pendingDaily > 0 && (
                          <button
                            onClick={onSettle}
                            className="flex items-center gap-1.5 text-xs bg-green-600 text-white px-3 py-1.5 rounded-md hover:bg-green-700 font-medium"
                          >
                            <Banknote size={13} />
                            Ureguluj ({formatAmount(
                              pendingDaily.toFixed(2),
                            )}{" "}
                            PLN)
                          </button>
                        )}
                        <button
                          onClick={onForgive}
                          className="flex items-center gap-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 text-gray-600 dark:text-gray-400 px-3 py-1.5 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/50 font-medium"
                        >
                          <XCircle size={13} />
                          Daruj karę
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ONE-TIME PENALTY SECTION */}
              {oneTimeTerms.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                    Kary jednorazowe (decyzja dyrektora)
                  </p>
                  <div className="space-y-2">
                    {oneTimeTerms.map((term) => (
                      <div
                        key={term.term_id}
                        className="flex items-center justify-between bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5"
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                            {term.description}
                          </p>
                          <p className="text-xs text-amber-700 mt-0.5">
                            Kwota: {formatAmount(term.calculated_amount)} PLN
                            {term.termination_possible && (
                              <span className="ml-2 font-medium text-red-600">
                                (zerwanie możliwe)
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="flex-shrink-0 ml-4">
                          {term.already_applied ? (
                            <span className="flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-md">
                              <CheckCircle2 size={12} /> Naliczona
                            </span>
                          ) : (
                            <button
                              onClick={() => onNalicz(term)}
                              className="text-xs bg-amber-600 text-white px-3 py-1.5 rounded-md hover:bg-amber-700 font-medium"
                            >
                              Nalicz karę
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Termination hint */}
              {row.termination_possible && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800">
                  <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">
                      Możliwe zerwanie zamówienia z winy podwykonawcy
                    </p>
                    <p className="text-xs text-red-600 mt-0.5">
                      Opóźnienie przekroczyło próg zerwania z umowy ramowej.
                      Akcja dostępna w szczegółach zamówienia.
                    </p>
                    <Link
                      to={`/orders/${row.order_id}`}
                      className="text-xs text-red-700 underline mt-1 inline-block"
                    >
                      Przejdź do zamówienia →
                    </Link>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
